import { differenceInDays } from 'date-fns';
import {
  EventDateLocation,
  EventTitle,
  EventTopics,
  LearnMoreButton,
  RegisterNowButton,
} from '@/app_components/Conferences/EventInfo';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';
import type { ConferencesEvent, EventsListEvent } from '@/app_lib/types/Conferences';
import { cn } from '@/app_lib/utils/utils';

const contentLayoutClass = 'flex flex-col layout-gap-xs justify-between h-full min-w-0';

export const EventInfoSecondary = ({
  event,
  past = false,
}: {
  event: EventsListEvent | ConferencesEvent;
  past?: boolean;
}) => {
  const daysAway = differenceInDays(new Date(event.startDate), new Date());
  const hasHoverState = !past && daysAway >= 0;

  const topBlock = ({ className }: { className?: string }) => (
    <>
      <EventDateLocation
        event={event}
        type='secondary'
      />
      <div className='flex flex-col gap-sm min-w-0'>
        <EventTopics event={event} />
        <EventTitle
          className={cn('line-clamp-2 md:line-clamp-3', className)}
          event={event}
        />
      </div>
    </>
  );

  const defaultButtons = (size: 'md' | 'lg' = 'md') => (
    <div className='flex gap-lg justify-center lg:justify-start w-full transition-none'>
      <LearnMoreButton
        className='w-full flex-1'
        externalLink={event.externalLink}
        size={size}
        type='tile'
        variant='secondaryOutline'
      />
      {!past && (
        <RegisterNowButton
          className='w-full flex-1'
          registerLink={event.registerLink}
          size={size}
          type='tile'
          variant='secondary'
        />
      )}
    </div>
  );

  const hoverButtons = (size: 'md' | 'lg' = 'md') => (
    <div className='flex gap-lg justify-center lg:justify-start w-full transition-none'>
      <LearnMoreButton
        className='w-full flex-1 hover:text-black! active:text-black!'
        externalLink={event.externalLink}
        size={size}
        type='tile'
        variant='whiteOutline'
      />
      {!past && (
        <RegisterNowButton
          className='w-full flex-1 text-black! hover:text-white! active:text-white!'
          registerLink={event.registerLink}
          size={size}
          type='tile'
          variant='secondarySpecialBlue'
        />
      )}
    </div>
  );

  if (!hasHoverState) {
    return (
      <div className='flex-1 w-full min-w-0 self-center h-full'>
        <div className={contentLayoutClass}>
          {topBlock({ className: 'xl:h-[9rem] lg:h-[8rem] md:h-[6.5rem] h-[4.3rem]' })}
          <div className='lg:max-xl:hidden'>{defaultButtons('lg')}</div>
          <div className='lg:max-xl:block hidden'>{defaultButtons('md')}</div>
        </div>
      </div>
    );
  }

  return (
    // The idea here is that we have two states: default and hover. To prevent CLS, we pretty much swap between the two states
    <div className='flex-1 w-full min-w-0 self-center h-full relative'>
      {/* Default state: title lower, no description, default buttons — visible when not hovered */}
      <div
        className={cn(
          contentLayoutClass,
          'opacity-100 group-hover:opacity-0 group-active:opacity-0 pointer-events-auto group-hover:pointer-events-none group-active:pointer-events-none',
        )}
      >
        {topBlock({ className: 'xl:h-[9rem] lg:h-[8rem] md:h-[6.5rem] h-[4.3rem]' })}
        <div className='lg:max-xl:hidden'>{defaultButtons('lg')}</div>
        <div className='lg:max-xl:block hidden'>{defaultButtons('md')}</div>
      </div>
      {/* Hover state: title higher, description, hover buttons — visible when hovered; no layout reflow */}
      <div
        className={cn(
          contentLayoutClass,
          'absolute inset-0 opacity-0 group-hover:opacity-100 group-active:opacity-100 pointer-events-none group-hover:pointer-events-auto group-active:pointer-events-auto',
        )}
      >
        {topBlock({ className: event.shortDescription ? '' : 'xl:h-[9rem] lg:h-[8rem] md:h-[6.5rem] h-[4.3rem]' })}
        {event.shortDescription && (
          <DescriptionParser
            className='text-secondary line-clamp-2 md:line-clamp-3'
            content={event.shortDescription}
          />
        )}
        <div className='lg:max-xl:hidden'>{hoverButtons('lg')}</div>
        <div className='lg:max-xl:block hidden'>{hoverButtons('md')}</div>
      </div>
    </div>
  );
};
